import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Typography,
  Alert,
  AlertTitle,
  Box,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Switch,
} from "@mui/material";
import { ANDatePicker } from "@answersnow/pkg-theme";
import { X } from "@phosphor-icons/react";
import AddIcon from "@mui/icons-material/Add";
import { dateWithoutTimezone } from "utils/dateWithoutTimezone";

interface UpdateAuthorizationDialogProps {
  open: boolean;
  updateAuthorizationCode: string;
  updateAuthorizationStartDate: Date | null;
  updateAuthorizationEndDate: Date | null;
  updateAuthorizationType: string;
  updateAuthorizationClinicianId: string;
  updatePendingAuth: boolean;
  updateApprovedUnits: any[];
  updateNewApprovedUnits: any[];
  saveButtonDisabled: boolean;
  availableInsuranceCodes: any[];
  clinicianList: any[];
  onChange: (name: string) => (e: any) => void;
  onChangeDate: (name: string, date: Date | null) => void;
  onCloseUpdateAuthorization: () => void;
  onSubmitUpdateAuthorization: () => void;
  onChangeUpdateApprovedUnitAmount: (index: number, e: any) => void;
  onAddUpdateNewApprovedUnit: () => void;
  onRemoveUpdateNewApprovedUnit: (index: number) => void;
  onChangeUpdateNewApprovedUnitCode: (index: number, e: any) => void;
  onChangeUpdateNewApprovedUnitAmount: (index: number, e: any) => void;
}

const UpdateAuthorizationDialog: React.FC<UpdateAuthorizationDialogProps> = ({
  open,
  updateAuthorizationCode,
  updateAuthorizationStartDate,
  updateAuthorizationEndDate,
  updateAuthorizationType,
  updateAuthorizationClinicianId,
  updatePendingAuth,
  updateApprovedUnits,
  updateNewApprovedUnits,
  saveButtonDisabled,
  availableInsuranceCodes,
  clinicianList,
  onChange,
  onChangeDate,
  onCloseUpdateAuthorization,
  onSubmitUpdateAuthorization,
  onChangeUpdateApprovedUnitAmount,
  onAddUpdateNewApprovedUnit,
  onRemoveUpdateNewApprovedUnit,
  onChangeUpdateNewApprovedUnitCode,
  onChangeUpdateNewApprovedUnitAmount,
}) => {
  const [sortedClinicians, setSortedClinicians] = useState<any[]>([]);

  useEffect(() => {
    if (clinicianList) {
      const sortedList = [...clinicianList].sort((a, b) =>
        a.first_name.localeCompare(b.first_name)
      );
      setSortedClinicians(sortedList);
    }
  }, [clinicianList]);

  const updatedApprovedUnitsHaveMissingInformation = updateApprovedUnits?.some(
    (unit) => !unit.code || !unit.codeId
  );
  const updateNewApprovedUnitsHaveMissingInformation = updateNewApprovedUnits?.some(
    (unit) => !unit.code || !unit.units || !unit.hours || !unit.codeId
  );

  const saveAuthDisabled =
    (!updateAuthorizationCode && !updatePendingAuth) ||
    !updateAuthorizationStartDate ||
    !updateAuthorizationEndDate ||
    !updateAuthorizationType ||
    saveButtonDisabled ||
    (updateAuthorizationType !== "secondary_auth" &&
      (!updateAuthorizationClinicianId ||
        updatedApprovedUnitsHaveMissingInformation ||
        (updateNewApprovedUnits.length > 0 && updateNewApprovedUnitsHaveMissingInformation)));

  const reAuthCodes = availableInsuranceCodes.filter((code: any) => !!code.re_auth);

  const authCodes =
    updateAuthorizationType === "re_auth"
      ? reAuthCodes
      : availableInsuranceCodes;

  const convertDate = (date: Date | null) => {
    if (!date) return null;
    return dateWithoutTimezone(new Date(date).toISOString().split("T")[0]);
  };

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      open={open}
      onClose={onCloseUpdateAuthorization}
      aria-labelledby="update-authorization-title"
    >
      <DialogTitle id="update-authorization-title">
        Edit Authorization Period
        <IconButton
          aria-label="close"
          onClick={onCloseUpdateAuthorization}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <X />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ zIndex: 2010 }}>
        <Grid container justifyContent="space-between" spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <TextField
              select
              label="Authorization type"
              id="auth-type"
              onChange={onChange("updateAuthorizationType")}
              value={updateAuthorizationType}
              disabled={updateAuthorizationType === "secondary_auth"}
              fullWidth
            >
              <MenuItem disabled value="">
                Select Type
              </MenuItem>
              <MenuItem value="pre_auth">Pre-Authorization</MenuItem>
              <MenuItem value="auth">Ongoing Authorization</MenuItem>
              <MenuItem
                value="secondary_auth"
                disabled={updateAuthorizationType !== "secondary_auth"}
              >
                Secondary Authorization
              </MenuItem>
              <MenuItem
                value="re_auth"
              >
                Re-Authorization
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6}>
            {updateAuthorizationType !== "secondary_auth" && (
              <TextField
                select
                label="Approved clinician"
                id="auth-clinician"
                onChange={onChange("updateAuthorizationClinicianId")}
                value={updateAuthorizationClinicianId}
                fullWidth
              >
                <MenuItem disabled value="">
                  Select clinician
                </MenuItem>
                {sortedClinicians?.map((clinician: any) => (
                  <MenuItem key={clinician.clinician_id} value={clinician.clinician_id}>
                    {clinician.first_name} {clinician.last_name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
          <Grid item xs={12} my={7}>
            <Alert
              action={
                <Switch
                  checked={updatePendingAuth}
                  onChange={onChange("updatePendingAuth")}
                  inputProps={{
                    "aria-label": "Temporary Authorization and Pending Credentials",
                  }}
                />
              }
              severity="info"
            >
              <AlertTitle>Temporary Authorization and Pending Credentials</AlertTitle>
              Turning on Temporary Authorization and Pending Credentials allows AnswersNow to see
              the client while authorizations and BCBA credentials are pending approvals
            </Alert>
          </Grid>
          <Grid item xs={4}>
            <ANDatePicker
              label="Start date"
              value={convertDate(updateAuthorizationStartDate)}
              onChange={(date: Date | null) => onChangeDate("updateAuthorizationStartDate", date)}
              placeholder="Pick a date"
              format="PP"
              slotProps={{
                field: { fullWidth: true },
                popper: { disablePortal: false, style: { zIndex: 4000 } },
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <ANDatePicker
              label="End date"
              value={convertDate(updateAuthorizationEndDate)}
              onChange={(date: Date | null) => onChangeDate("updateAuthorizationEndDate", date)}
              placeholder="Pick a date"
              format="PP"
              slotProps={{
                field: { fullWidth: true },
                popper: { disablePortal: false, style: { zIndex: 4000 } },
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              id="authorizationCode"
              placeholder="Enter authorization code"
              label="Authorization number"
              onChange={onChange("updateAuthorizationCode")}
              value={updateAuthorizationCode}
              variant="outlined"
              fullWidth
              disabled={updatePendingAuth}
            />
          </Grid>
        </Grid>

        {updateAuthorizationType !== "secondary_auth" &&
          (updateApprovedUnits.length > 0 || updateNewApprovedUnits.length > 0) && (
            <Card variant="outlined" style={{ margin: "24px 0" }}>
              <Grid container style={{ backgroundColor: "#f2f2f2" }}>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={3} style={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="subtitle1" style={{ padding: "16px" }}>
                        Billing code
                      </Typography>
                    </Grid>
                    <Grid item xs={3} style={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="subtitle1" style={{ padding: "16px" }}>
                        Number of units
                      </Typography>
                    </Grid>
                    <Grid item xs={3} style={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="subtitle1" style={{ padding: "16px" }}>
                        Number of hours
                      </Typography>
                    </Grid>
                    <Grid item xs={3} style={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        variant="subtitle1"
                        style={{ padding: "16px", paddingLeft: "32px" }}
                      >
                        Action
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {updateApprovedUnits.map((unit, i) => (
                <React.Fragment key={`proved-${i}`}>
                  <Grid container>
                    <Grid item xs={12}>
                      <Grid container spacing={2}>
                        <Grid item xs={3}>
                          <TextField
                            select
                            id="type"
                            disabled
                            value={unit.codeId}
                            fullWidth
                            style={{ padding: "16px 0 16px 16px" }}
                          >
                            <MenuItem disabled value="">
                              Select a code
                            </MenuItem>
                            {availableInsuranceCodes.map((code: any, index: number) => (
                              <MenuItem key={index} value={code.id}>
                                {code.insurance_code}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            id="outlined-helperText"
                            disabled={!unit.code}
                            placeholder="Enter number of units"
                            value={unit.units}
                            type="number"
                            fullWidth
                            InputProps={{ inputProps: { min: 0 } }}
                            style={{ padding: "16px 0 16px 16px" }}
                            onChange={(e) => onChangeUpdateApprovedUnitAmount(i, e)}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            id="outlined-helperText"
                            disabled
                            placeholder="Enter number of hours"
                            value={unit.hours}
                            type="number"
                            style={{ padding: "16px 0 16px 16px" }}
                            fullWidth
                          />
                        </Grid>
                        <Grid
                          item
                          xs={3}
                          style={{
                            display: "flex",
                            justifyContent: "left",
                            alignItems: "center",
                          }}
                        >
                          <Box style={{ padding: "16px 0 16px 16px" }}>
                            <Button
                              variant="text"
                              color="error"
                              onClick={() => onRemoveUpdateNewApprovedUnit(i)}
                              disabled={true}
                            >
                              Delete
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                      <Divider />
                    </Grid>
                  </Grid>
                </React.Fragment>
              ))}

              {updateNewApprovedUnits &&
                updateNewApprovedUnits.length > 0 &&
                updateNewApprovedUnits.map((unit, i) => (
                  <React.Fragment key={`updated-${i}`}>
                    <Grid container>
                      <Grid item xs={12}>
                        <Grid container spacing={2}>
                          <Grid item xs={3}>
                            <TextField
                              select
                              id="type"
                              onChange={(e) => onChangeUpdateNewApprovedUnitCode(i, e)}
                              value={unit.code}
                              fullWidth
                              style={{ padding: "16px 0 16px 16px" }}
                            >
                              <MenuItem disabled value="">
                                Select a code
                              </MenuItem>
                              {authCodes.map((code: any, index: number) => (
                                <MenuItem key={index} value={code.insurance_code}>
                                  {code.insurance_code}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                          <Grid item xs={3}>
                            <TextField
                              id="outlined-helperText"
                              disabled={!unit.code}
                              placeholder="Enter number of units"
                              value={unit.units}
                              type="number"
                              fullWidth
                              InputProps={{ inputProps: { min: 0 } }}
                              onChange={(e) => onChangeUpdateNewApprovedUnitAmount(i, e)}
                              style={{ padding: "16px 0 16px 16px" }}
                            />
                          </Grid>
                          <Grid item xs={3}>
                            <TextField
                              id="outlined-helperText"
                              disabled
                              placeholder="Enter number of hours"
                              value={unit.hours}
                              type="number"
                              fullWidth
                              style={{ padding: "16px 0 16px 16px" }}
                            />
                          </Grid>
                          <Grid
                            item
                            xs={3}
                            style={{
                              display: "flex",
                              justifyContent: "left",
                              alignItems: "center",
                            }}
                          >
                            <Box style={{ padding: "16px 0 16px 16px" }}>
                              <Button
                                color="error"
                                variant="text"
                                onClick={() => onRemoveUpdateNewApprovedUnit(i)}
                              >
                                Delete
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                        <Divider />
                      </Grid>
                    </Grid>
                  </React.Fragment>
                ))}
            </Card>
          )}

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
          {updateAuthorizationType !== "secondary_auth" ? (
            <Button
              onClick={onAddUpdateNewApprovedUnit}
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
            >
              Add billing code
            </Button>
          ) : (
            <div />
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={onCloseUpdateAuthorization} variant="outlined" color="inherit">
              Cancel
            </Button>
            <Button
              disabled={saveAuthDisabled}
              onClick={onSubmitUpdateAuthorization}
              variant="contained"
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateAuthorizationDialog;
